/**
 * Long-form pillar content for content-authority SEO.
 *
 * Route:  /:slug   (see App.js — mounted directly at root, not nested)
 * Component: /src/pages/PillarArticle.jsx
 *
 * Each entry produces a single article-style page with:
 *   • Hero (H1 + dek)
 *   • TL;DR summary box (AEO / featured-snippet fodder)
 *   • Structured sections with H2 / H3 headings
 *   • FAQ block (schema-ready)
 *   • Related content links (topical clustering)
 *   • Sticky CTA to /contact
 *
 * These pages target high-intent bottom-of-funnel queries:
 *   • "how much does a brand film cost in india"
 *   • "production studio vs ad agency"
 *   • "freelance video crew vs production house"
 *   • "in-house video team vs external production"
 *
 * ADD NEW PILLAR:
 *   1. Append an entry keyed by slug below.
 *   2. Add the slug to the sitemap in /app/backend/server.py.
 *   3. Add the slug + meta to STATIC_ROUTES in /app/frontend/scripts/generate-seo-html.mjs.
 *   4. Add a link in Footer.jsx → Resources column.
 */
export const PILLAR_ARTICLES = {
  "brand-film-cost-india": {
    metaTitle:
      "How Much Does A Brand Film Cost In India? · A Frank Guide (2026) · Relatable Studio",
    metaDescription:
      "The real drivers of brand film production cost in India — crew, script, cast, location, gear, post — explained by a Bangalore studio that has delivered 1,600+ projects. No fake price lists, just the variables that move a quote.",
    category: "Pricing Guide",
    eyebrow: "resources · pricing",
    h1a: "How much does a brand film",
    h1b: "cost in India?",
    dek: "There is no honest answer that fits in one number. But there is a framework for how the number gets built. This is that framework, from a studio that has quoted 1,600+ films.",
    // TL;DR block — the exact answer AI engines will lift verbatim.
    tldr: [
      "Brand film cost in India is driven by six variables — crew size, script complexity, cast, locations, camera package and post-production. Not by page count or logo count.",
      "A one-day, one-location, interview-led brand film with a small crew sits at the entry point. A multi-day, multi-location, director-DP-led narrative film with cast, art direction, colour, sound design and social cutdowns sits at the top.",
      "The right way to scope a brand film is to work backwards from the business problem — reach, hiring, launch, positioning — not from a shot list.",
    ],
    sections: [
      {
        h2: "Why price ranges from other websites mislead you",
        body: [
          "Most agency price lists in India publish a per-minute rate — ₹1L per minute, ₹2L per minute — which is a nostalgic pricing model from the TV commercial era. It has almost nothing to do with what a brand film actually costs to make in 2026.",
          "A 60-second corporate montage shot in a lobby with two lights is a completely different production from a 60-second brand film with a scripted narrative, actors, a location, and a director. The runtime is identical. The cost is 8-15x different.",
          "The honest question is not 'how long is the film?' — it is 'what does this film have to change?'",
        ],
      },
      {
        h2: "The six variables that actually move the number",
        subsections: [
          {
            h3: "1. Crew composition",
            body:
              "A three-person crew — director/camera, sound, producer — will land in a very different band than a fifteen-person crew with a director, DP, gaffer, sound recordist, focus puller, art department, PA support and a producer. Neither is inherently right. What matters is whether the film's ambition needs the extra hands.",
          },
          {
            h3: "2. Script complexity",
            body:
              "An interview-led film with pre-written questions and no VO script is faster and cheaper than a fully scripted narrative with dialogue, blocking and characters. The moment a film needs cast — even one actor — the day is longer, the crew is bigger, and the number moves.",
          },
          {
            h3: "3. Cast",
            body:
              "Cast fees in India range from ₹0 (real employees, unpaid interviewees) to seven-figure day rates (name talent). Between those extremes sit trained non-professional talent, character actors, and micro-influencers with regional followings. Cast alone can move a budget 30-50% either direction.",
          },
          {
            h3: "4. Locations",
            body:
              "Studio shoots are the most controllable and often the cheapest per shoot-day. On-location shoots add permits, transport, catering for larger crews, and weather insurance. Multi-city shoots add flights, hotels and a second local unit. Every 'we'd love it shot in Kerala' idea has a real logistics cost attached.",
          },
          {
            h3: "5. Camera package",
            body:
              "The camera itself is a rounding error on most brand-film budgets. What actually costs money is the LENS PACKAGE, LIGHTING PACKAGE and GRIP PACKAGE that go with it. A cinema-grade Alexa/Venice/RED shoot with primes and a proper lighting truck is real money. Mirrorless-based documentary shooting with LEDs is a fraction. Both can produce beautiful work — the question is which one the story needs.",
          },
          {
            h3: "6. Post-production",
            body:
              "Edit days, colour grading, sound design, VO recording, motion graphics, subtitling, deliverable versions — post is often 30-40% of a brand film's real cost. A one-cut delivery is cheap. A hero film plus 15 social cutdowns, three regional-language versions and a 15-second TVC edit is not.",
          },
        ],
      },
      {
        h2: "What drives the film up — and what drives it down",
        body: [
          "Films that get expensive fast: name cast, celebrity director, night shoots, drone/underwater/aerial units, cross-country travel, 4K+ deliverables in every aspect ratio, subtitled versions in multiple languages, VFX-heavy work, custom music composition, prosthetics/period costume.",
          "Films that stay lean: single location, single day, real people not actors, natural light or minimal LED kit, English-only delivery, one primary deliverable plus captions, use of existing brand assets for graphics, licensed stock music.",
          "Neither list is 'better'. Choose based on what the film has to do, not on what looks impressive in a treatment deck.",
        ],
      },
      {
        h2: "The right way to scope a brand film cost in Bangalore",
        body: [
          "Start with the business outcome. A film that has to fill a hiring pipeline is not the same investment as one that has to reposition a category. The outcome determines the ambition; the ambition determines the crew.",
          "Then map the constraints. A three-week deadline forces production choices. A one-day availability window from the founder forces script choices. A ₹8L budget forces crew choices. Constraints are not enemies of good work — they are the shape of the work.",
          "Only then discuss the number. Anyone quoting before this conversation is guessing.",
        ],
      },
      {
        h2: "How Relatable Studio scopes cost",
        body: [
          "We do not maintain a rate card. We maintain a scoping conversation — usually 30 minutes — after which we send a written treatment with three cost options: one lean, one standard, one ambitious. Same story, three levels of production. You choose which trade-off makes sense.",
          "That gets around the two failure modes of Indian production pricing: (a) sticker shock from a single inflated quote, and (b) a race to the bottom where the film ships but the story doesn't land.",
          "You can start that conversation at nexa@relatable.studio, WhatsApp +91 93800 21206, or the brief form on the contact page. Response time is within 24 hours, often within 4.",
        ],
      },
    ],
    faqs: [
      {
        q: "What is the entry point for a professional brand film in India?",
        a: "A single-day, single-location, small-crew interview-led film with basic edit and colour delivery is the entry point. Any studio quoting below that band is either using free interns, borrowing gear informally, or is a freelance one-person operation — none of which are inherently wrong, but the number should be understood in context.",
      },
      {
        q: "Does a longer film always cost more?",
        a: "No. A 3-minute interview film shot in one location is often cheaper than a 30-second brand ad film with cast, art direction and a location. Runtime is not the cost driver. Production complexity is.",
      },
      {
        q: "Can we get a rough estimate before a full scoping call?",
        a: "Yes. Send a one-line brief — 'brand film, launch, one shoot day, Bangalore, three cast members' — and we will respond with a three-tier band within 24 hours. The full quote follows the scoping call.",
      },
      {
        q: "Does Relatable Studio work with fixed-price agency retainers?",
        a: "No. Every engagement is scoped per film. This keeps production honest and lets founders opt out of the invisible retainer mark-up that traditional agencies pass through on production costs.",
      },
      {
        q: "Why is post-production often the biggest cost line?",
        a: "Because it is the longest phase. A single shoot day generates 2-3 weeks of edit, colour, sound and revisions. Post also multiplies with every deliverable — social cutdowns, regional versions, subtitles, aspect ratios. Studios that underestimate post are the ones that go over-budget.",
      },
    ],
    related: [
      { name: "Brand Film Production in Bangalore", href: "/services/brand-film-production-bangalore" },
      { name: "Corporate Film Production in Bangalore", href: "/services/corporate-film-production-bangalore" },
      { name: "Employer Branding Films in Bangalore", href: "/services/employer-branding-films-bangalore" },
      { name: "Relatable vs Traditional Ad Agency", href: "/relatable-vs-ad-agency" },
    ],
  },

  "relatable-vs-ad-agency": {
    metaTitle:
      "Production Studio vs Ad Agency · When To Choose Which · Relatable Studio",
    metaDescription:
      "An ad agency owns the campaign brief. A production studio owns the craft. This guide explains when to work with a Bangalore production studio (like Relatable) directly, and when you still need an ad agency.",
    category: "Comparison",
    eyebrow: "resources · comparison",
    h1a: "Production studio",
    h1b: "vs ad agency.",
    dek: "Not a rivalry — a role split. Understanding it saves you an entire tier of mark-up when it doesn't need to exist, and saves you a botched campaign when it does.",
    tldr: [
      "Ad agencies own strategy, positioning, campaign architecture and paid-media planning. Production studios own script, direction, craft and delivery.",
      "For 60% of brand-film briefs — founder stories, product films, employer branding, corporate milestone films — a production studio can be engaged directly. No agency layer needed.",
      "For 40% — large-scale category campaigns, multi-touchpoint launches, national TVC pushes — an agency's strategic and media-buying muscle is worth its fee.",
    ],
    sections: [
      {
        h2: "What each one is actually built for",
        subsections: [
          {
            h3: "The traditional ad agency",
            body:
              "An ad agency is a strategy + creative + media operation. Its P&L is built on retainer fees and media-buying commissions. Production is typically outsourced to a studio, with an agency mark-up (usually 15-30%) added on top.",
          },
          {
            h3: "The production studio",
            body:
              "A production studio is a craft + delivery operation. Its P&L is built on project fees, no retainers. It owns the crew, the gear (or the vendor relationships), the edit suite, the colourist, the sound engineer. It is where the film actually gets made.",
          },
        ],
      },
      {
        h2: "When you should engage a production studio directly",
        body: [
          "Founder storytelling films. The founder is the strategist — you do not need an external strategy layer.",
          "Employer branding films. Your HR/talent team knows the hiring problem better than any agency; a studio helps them tell it well.",
          "Product films and SaaS explainers. The product team owns the message; a studio owns the translation into film.",
          "Corporate milestone and AGM films. Internal narrative, internal decision-maker — no external strategy needed.",
          "Recurring content — series, culture films, event aftermovies. Repeat production benefits from a studio that learns your brand.",
        ],
      },
      {
        h2: "When you still need an ad agency",
        body: [
          "Category-defining launches. When the brand itself is being repositioned and a new visual/verbal identity is being built, agency strategy is invaluable.",
          "Multi-touchpoint campaigns. When a single idea has to land across TVC, digital, OOH, print, radio, sponsorship and PR simultaneously, agencies are built for the orchestration.",
          "National paid-media pushes. Media buying scale, GRP negotiation and CPP optimisation are agency skills a studio does not have.",
          "Regulatory-heavy sectors. Pharma, BFSI, insurance — legal review chains benefit from agency account management as a buffer.",
        ],
      },
      {
        h2: "How Relatable Studio works with agencies (and without them)",
        body: [
          "About half our work is directly with brands. The other half is production partnerships with agencies — we execute their creative, staying in our lane, no upstream strategy conflict.",
          "For brands going direct, we take on the scripting and direction that would otherwise sit inside the agency. For agencies, we are the production spine that turns their creative into a shipped film. Both models work.",
          "The wrong model is the one where a brand pays agency mark-up on production for a film the agency did not conceptually add value to. That is the mark-up worth eliminating.",
        ],
      },
    ],
    faqs: [
      {
        q: "Is a production studio cheaper than an ad agency for the same film?",
        a: "For the production component, yes — you skip the 15-30% agency mark-up passed through on production costs. For strategy and campaign architecture, an agency delivers value a studio does not.",
      },
      {
        q: "Can Relatable Studio also do the creative strategy?",
        a: "For film-specific creative — script, narrative, tonal direction — yes, and we do this on most direct-with-brand engagements. For brand-level positioning strategy, an ad agency or an independent brand strategist is a better fit.",
      },
      {
        q: "Do you work with agencies as a production partner?",
        a: "Yes. About 40% of our projects are agency partnerships. We execute the creative, do not compete for the strategy work, and share credit transparently.",
      },
      {
        q: "What do we lose if we skip the agency layer?",
        a: "Media buying scale, multi-touchpoint campaign orchestration, and an external account management buffer. For a single film with a defined internal owner, you lose almost nothing.",
      },
    ],
    related: [
      { name: "Relatable vs Freelance Video Crew", href: "/relatable-vs-freelance-crew" },
      { name: "In-house Video Team vs External Production", href: "/in-house-vs-external-production" },
      { name: "How much does a brand film cost in India?", href: "/brand-film-cost-india" },
      { name: "Brand Film Production in Bangalore", href: "/services/brand-film-production-bangalore" },
    ],
  },

  "relatable-vs-freelance-crew": {
    metaTitle:
      "Production Studio vs Freelance Video Crew · When Each Makes Sense · Relatable Studio",
    metaDescription:
      "Stitching together freelance videographers, editors and colourists can save money — until it doesn't. A Bangalore production studio's guide to when a freelance crew works and when it costs more than it saves.",
    category: "Comparison",
    eyebrow: "resources · comparison",
    h1a: "Production studio",
    h1b: "vs freelance crew.",
    dek: "A stitched-together freelance crew can save 20-30% on the surface. Whether it also costs you the film is a different question entirely.",
    tldr: [
      "A freelance crew works well for one-off, low-stakes, low-craft work — a talking-head interview, a simple event coverage, a social-first product demo.",
      "A production studio wins on repeatability, insurance risk, cohesion of vision, and post-production handoff — precisely where multi-day, multi-deliverable brand films break.",
      "The hidden cost of freelance is not the day rate. It is the project management, the handoff failures, and the missing pieces of gear that surface at 6am on shoot day.",
    ],
    sections: [
      {
        h2: "What a freelance crew is optimised for",
        body: [
          "A freelance crew is a good fit when the brief is contained, the deliverable is single, and the client has the internal muscle to project-manage.",
          "A single freelance DP with a mirrorless kit can produce excellent one-day interview films. A freelance editor can turn around a social cut in 48 hours. These are real strengths.",
          "The freelance model breaks when the film needs more than one moving part — when scripting, casting, art direction, colour and sound all have to hand off to each other cleanly.",
        ],
      },
      {
        h2: "Where a studio's structure earns its fee",
        subsections: [
          {
            h3: "1. Insurance and risk",
            body:
              "Studios carry professional indemnity, public liability and gear insurance. A freelancer usually carries none of that. On a corporate campus, factory, or restricted-access shoot, the client's legal team will ask. A studio has the answer; a freelancer does not.",
          },
          {
            h3: "2. Continuity of vision",
            body:
              "Freelance crews assemble per project. Every project restarts with 'how do you like to work'. A studio holds institutional memory — treatment templates, review cadences, colour pipelines, sound room presets — that compound across projects.",
          },
          {
            h3: "3. Backup gear and backup people",
            body:
              "When a camera dies at 10am, a studio has a second body in the van. When an editor is sick, a studio has a second editor who has done work like it before. Freelance production has neither of these safety nets.",
          },
          {
            h3: "4. Post-production handoff",
            body:
              "The single most common failure mode of a freelance-built film is the edit → colour → sound handoff. Each pass loses context. A studio's post team is under one roof (or one process), and the handoff is a document, not a Slack message.",
          },
        ],
      },
      {
        h2: "The real economics",
        body: [
          "A freelance crew's day rate is 20-30% lower than a studio's equivalent day rate. That is true.",
          "The gap closes fast once you add: project management hours (usually invisible until you're in it), gear rental you thought was included, revision rounds that were not scoped, and delivery-format multiplication (aspect ratios, subtitles, versions).",
          "For a two-shoot-day brand film with 4-5 deliverables, the freelance route often ends within 5-10% of the studio route — with none of the risk padding.",
        ],
      },
      {
        h2: "How to decide",
        body: [
          "Choose a freelance crew when the film is one-and-done, single-location, single-deliverable, low-stakes, and you have someone on your team who can project-manage.",
          "Choose a studio when the film is multi-day, multi-deliverable, high-stakes (external campaign, executive-visible, hiring-critical), or when the project management would fall on you.",
        ],
      },
    ],
    faqs: [
      {
        q: "Does Relatable Studio ever work with freelancers?",
        a: "Yes — we work with 700+ freelance creative partners. The difference is that we integrate them into a studio process (treatments, review rounds, post pipeline) rather than leaving the client to manage them individually.",
      },
      {
        q: "Can we mix — hire a freelance DP but a studio for post?",
        a: "Yes, and it's a common model for cost-sensitive projects. The risk is the handoff — the DP's LUTs, framing choices and audio setup have to survive to the studio's post pipeline intact. We often provide the LUT + brief to the freelance DP before shoot day to reduce this risk.",
      },
      {
        q: "What percent of Bangalore brand films are freelance-crewed?",
        a: "Anecdotally, over half of sub-₹5L films in Bangalore are freelance-crewed. Above ₹8L, studio-crewed becomes the norm — mostly because that is where risk, insurance and multi-deliverable complexity start to matter.",
      },
    ],
    related: [
      { name: "Relatable vs Traditional Ad Agency", href: "/relatable-vs-ad-agency" },
      { name: "In-house Video Team vs External Production", href: "/in-house-vs-external-production" },
      { name: "How much does a brand film cost in India?", href: "/brand-film-cost-india" },
    ],
  },

  "in-house-vs-external-production": {
    metaTitle:
      "In-house Video Team vs External Production House · Build or Buy? · Relatable Studio",
    metaDescription:
      "When does hiring a full-time in-house video team beat working with an external production house in India? A Bangalore studio's guide to the math, the failure modes, and the hybrid model that usually wins.",
    category: "Comparison",
    eyebrow: "resources · comparison",
    h1a: "In-house video team",
    h1b: "vs external production.",
    dek: "The question you should be asking is not build-or-buy. It is which parts to build, which parts to buy, and how they hand off to each other.",
    tldr: [
      "In-house video teams win on speed, repeatability, and cultural fluency — perfect for high-volume internal content, event coverage, social-first content and rapid social response.",
      "External production houses win on craft ceiling, project peaks, and one-off strategic films — where the ambition briefly exceeds what a small in-house team can carry.",
      "The best-performing content operations are hybrid: a small in-house team owning volume, an external studio partnering on peaks.",
    ],
    sections: [
      {
        h2: "The math of building an in-house team",
        body: [
          "A minimum viable in-house video team in India — one video producer, one editor, one motion designer, one shooter — is a ₹80L-1.2Cr annual salary bill, plus ~₹20L in gear, plus office overhead.",
          "That team can produce roughly 8-15 films a month at social/mid-tier quality if scoped tightly. Beyond that, quality collapses or overtime becomes structural.",
          "For that math to work, you need enough steady internal demand — 100+ films a year — to justify the fixed cost. Startups and mid-market brands almost never have that. Enterprises sometimes do.",
        ],
      },
      {
        h2: "What in-house teams are structurally good at",
        body: [
          "Rapid response — a news cycle, a competitor launch, a market event. External production cannot beat internal turnaround.",
          "Cultural authenticity — an internal team knows the codebase, the release cadence, the internal jokes. External production has to be briefed each time.",
          "High-volume, low-differentiation content — social cutdowns, employer content, event coverage. Repetition benefits from a permanent team.",
        ],
      },
      {
        h2: "Where external production earns its fee",
        body: [
          "Craft ceilings — when the brief needs cinematic direction, name cast, or narrative-driven storytelling that a general in-house team is not staffed for.",
          "Project peaks — the annual launch, the fundraise film, the anniversary film. Ambition briefly spikes above in-house capacity.",
          "Objectivity — sometimes the founder's story lands better when told by an outside team that can push back on internal narrative fatigue.",
          "Insurance-heavy shoots — factory, campus, restricted-access production is easier to insure through an external studio.",
        ],
      },
      {
        h2: "The hybrid model that usually wins",
        body: [
          "A small in-house team (1-3 people) covering 80% of volume — social, internal, event.",
          "A retained (or panel-listed) external production house for the top 20% — launch films, employer branding hero films, founder stories, category campaigns.",
          "A single content calendar shared between both, with a hand-off protocol for when a project graduates from in-house to external. This is how most mature Indian content operations run.",
        ],
      },
    ],
    faqs: [
      {
        q: "At what scale should a company build an in-house video team?",
        a: "Roughly 100+ films per year of steady demand. Below that, the math almost never works — the fixed salary cost exceeds the flexible external production cost. This is why most startups, D2C brands and mid-market SaaS companies stay hybrid or fully external for years.",
      },
      {
        q: "Does Relatable Studio work with clients that have in-house teams?",
        a: "Yes — many of our best client relationships involve an in-house team that owns volume, and us handling their two or three annual peak projects. We often provide creative direction and post-production support even when the shoot is in-house.",
      },
      {
        q: "What does it cost to keep a production studio on retainer?",
        a: "We do not offer traditional retainers — every project is scoped independently. What we do offer is a preferred-panel arrangement for brands doing 4+ projects a year, with pre-agreed rate cards and reserved calendar capacity. This has most of the retainer benefits without the retainer overhead.",
      },
    ],
    related: [
      { name: "Relatable vs Traditional Ad Agency", href: "/relatable-vs-ad-agency" },
      { name: "Relatable vs Freelance Video Crew", href: "/relatable-vs-freelance-crew" },
      { name: "How much does a brand film cost in India?", href: "/brand-film-cost-india" },
    ],
  },
};
